import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save } from "lucide-react";
import ContentLoader from "@/authentication/ContentLoader";
import { db, auth } from "../../firebase/config";
import { collection, doc, setDoc, getDocs, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";

// Define interfaces
interface Affiliation {
  role: string;
  schoolID?: string;
}

interface Teacher {
  id: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  employeeId: string;
  position: string;
  phone?: string;
  email?: string;
}

interface Dorm {
  id: string;
  name: string;
  location: string;
  capacity: number;
  occupied: number;
  available: number;
  patronTeacherId?: string | null;
  schoolID: string;
  dormUid: string;
  submittedBy: string;
  createdAt: Date;
}

interface DormEditFormProps {
  dorm: Dorm;
  onBack: () => void;
  onSave: (updatedDorm: Dorm) => void;
}

const DormEditForm = ({ dorm, onBack, onSave }: DormEditFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Auth / school state
  const [userId, setUserId] = useState<string | null>(null);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: dorm.name || "",
    location: dorm.location || "",
    capacity: dorm.capacity?.toString() || "0",
    patronTeacherId: dorm.patronTeacherId || null,
  });

  // Teachers
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(true);

  /* ── Auth & Claims Verification ──────────────────────────────────── */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        try {
          await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult(true);
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          if (process.env.NODE_ENV !== "production") {
            console.log("ID Token Result claims:", JSON.stringify(idTokenResult.claims, null, 2));
          }

          let selectedAffiliation: Affiliation | null = null;
          if (defaultAffiliation?.role === "admin" && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => aff.role === "admin" && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            setSchoolID(selectedAffiliation.schoolID);
            setHasValidClaims(true);
          } else {
            // Retry after delay to handle claim propagation
            await new Promise(resolve => setTimeout(resolve, 10000));
            await user.getIdToken(true);
            const retryIdTokenResult = await user.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            selectedAffiliation = retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID
              ? retryDefaultAff
              : retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID) || null;

            if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID && retrySchoolID === selectedAffiliation.schoolID) {
              setSchoolID(selectedAffiliation.schoolID);
              setHasValidClaims(true);
            } else {
              throw new Error("Invalid admin claims after retry");
            }
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          toast({
            title: "Authentication Error",
            description: "Failed to verify permissions. Please log in again.",
            variant: "destructive",
            duration: 5000,
          });
          navigate("/login-page");
        }
      } else {
        setUserId(null);
        setSchoolID(null);
        setHasValidClaims(false);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  /* ── Fetch Teachers ───────────────────────────────────── */
  const fetchTeachers = useCallback(async () => {
    if (!hasValidClaims || !schoolID) {
      setIsLoadingTeachers(false);
      return;
    }

    setIsLoadingTeachers(true);
    try {
      const teachersRef = collection(db, `schools/${schoolID}/teachers`);
      const teacherSnapshot = await getDocs(teachersRef);
      const teacherList: Teacher[] = teacherSnapshot.docs.map((doc) => ({
        id: doc.id,
        firstName: doc.data().firstName || "",
        lastName: doc.data().lastName || "",
        middleName: doc.data().middleName || undefined,
        employeeId: doc.data().employeeId || "",
        position: doc.data().teacherType || "",
        phone: doc.data().phoneNumber || undefined,
        email: doc.data().email || undefined,
      }));
      setTeachers(teacherList);
      if (process.env.NODE_ENV !== "production") {
        console.log("Fetched Teachers:", teacherList);
      }
    } catch (error: any) {
      console.error("Error fetching teachers:", error);
      toast({
        title: "Error Loading Teachers",
        description: `Failed to load teacher data: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsLoadingTeachers(false);
    }
  }, [schoolID, hasValidClaims, toast]);

  useEffect(() => {
    if (!isAuthLoading && hasValidClaims && schoolID) {
      fetchTeachers();
    } else {
      setIsLoadingTeachers(false);
    }
  }, [isAuthLoading, hasValidClaims, schoolID, fetchTeachers]);

  /* ── Input Change Handler ───────────────────────────────── */
  const handleInputChange = (field: keyof typeof formData, value: string | null) => {
    setFormData((prev) => {
      if (field === "capacity") {
        return {
          ...prev,
          [field]: value || "",
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  /* ── Submit Handler ─────────────────────────────────────── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!schoolID || !userId || !hasValidClaims) {
      toast({
        title: "Authentication Error",
        description: "User, school ID, or permissions missing. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "User is not authenticated. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    const nameTrimmed = formData.name.trim();
    const locationTrimmed = formData.location.trim();
    const capacity = parseInt(formData.capacity);

    if (!nameTrimmed || !locationTrimmed) {
      toast({
        title: "Invalid Input",
        description: "Dormitory name and location cannot be empty.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    if (isNaN(capacity) || capacity <= 0) {
      toast({
        title: "Invalid Capacity",
        description: "Dormitory capacity must be a positive number.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    if (capacity < dorm.occupied) {
      toast({
        title: "Invalid Capacity",
        description: `Capacity cannot be less than the number of current occupants (${dorm.occupied}).`,
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      // Check for duplicate dorm name (excluding current dorm)
      if (nameTrimmed !== dorm.name) {
        // Only check for duplicates if the name has changed
        const dormsCollectionRef = collection(db, `schools/${schoolID}/dorms`);
        const q = query(dormsCollectionRef, where("name", "==", nameTrimmed));
        const querySnapshot = await getDocs(q);
        const otherDormsWithSameName = querySnapshot.docs.filter(doc => doc.id !== dorm.dormUid);

        if (otherDormsWithSameName.length > 0) {
          toast({
            title: "Dorm Name Already Exists",
            description: `A dormitory named "${nameTrimmed}" already exists. Please choose a different name.`,
            variant: "destructive",
            duration: 5000,
          });
          return;
        }
      }

      // Prepare updated dorm data
      const updatedDorm: Dorm = {
        ...dorm,
        name: nameTrimmed,
        location: locationTrimmed,
        capacity,
        available: capacity - (dorm.occupied || 0),
        patronTeacherId: formData.patronTeacherId,
        submittedBy: userId,
      };

      // Update Firestore
      const dormDocRef = doc(db, `schools/${schoolID}/dorms`, dorm.dormUid);
      await setDoc(dormDocRef, updatedDorm, { merge: true });

      toast({
        title: "Dormitory Updated",
        description: `${nameTrimmed} has been updated successfully.`,
        duration: 3000,
      });

      onSave(updatedDorm);
      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error: any) {
      console.error("Error updating dormitory:", error);
      toast({
        title: "Update Failed",
        description: `Failed to update dormitory: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  /* ── Loading UI handled by ContentLoader ─────────────────────────────── */

  /* ── Unauthorized UI ───────────────────────────────────── */
  if (!isAuthLoading && !hasValidClaims) {
    return (
      <div className="flex w-full h-full justify-center items-center p-6 bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md border-none bg-white dark:bg-gray-800">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl text-gray-900 dark:text-white">
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-400">
              You do not have the necessary permissions to edit a dormitory. Please contact support or re-login.
            </p>
            <Button
              onClick={() => navigate("/login-page")}
              className="mt-4 bg-blue-600 text-white hover:bg-blue-700"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ── Main UI ───────────────────────────────────────────── */
  return (
    <ContentLoader isLoading={isAuthLoading || (hasValidClaims && isLoadingTeachers)} message={isLoadingTeachers ? "Loading teachers..." : "Loading dormitory edit form..."}>
      <div className="w-full h-full bg-gray-50 dark:bg-gray-900">
        <div className="mb-6">
        <Button
          onClick={onBack}
          variant="outline"
          className="mb-4 flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dormitories
        </Button>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Edit Dormitory
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Update dormitory information
        </p>
      </div>

      <Card className="max-w-2xl border-none bg-white dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl text-gray-900 dark:text-white">
            Dormitory Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-gray-900 dark:text-white">
                  Dormitory Name
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter dormitory name"
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location" className="text-sm font-medium text-gray-900 dark:text-white">
                  Location
                </Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleInputChange("location", e.target.value)}
                  placeholder="Enter dormitory location"
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="capacity" className="text-sm font-medium text-gray-900 dark:text-white">
                  Capacity
                </Label>
                <Input
                  id="capacity"
                  type="number"
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => handleInputChange("capacity", e.target.value)}
                  placeholder="Enter dormitory capacity"
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="patron" className="text-sm font-medium text-gray-900 dark:text-white">
                  Dorm Patron (Optional)
                </Label>
                <Select
                  value={formData.patronTeacherId || "none"}
                  onValueChange={(value) => handleInputChange("patronTeacherId", value === "none" ? null : value)}
                >
                  <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectValue placeholder="Select a teacher as dorm patron" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                    <SelectItem
                      value="none"
                      className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      No Patron Assigned
                    </SelectItem>
                    {teachers.map((teacher) => (
                      <SelectItem
                        key={teacher.id}
                        value={teacher.id}
                        className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {teacher.firstName} {teacher.lastName} ({teacher.employeeId})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                className="border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700"
              >
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  </ContentLoader>
  );
};

export default DormEditForm;